import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { SupabaseClient } from '@supabase/supabase-js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ProductFilters, PaginatedResponse } from '../types'; // Product se reemplazará
import type { CatalogProduct } from '../types/catalog'; // Importar CatalogProduct
import { transformRawProductToCatalogProduct, RawProductDataForSupabase } from '../utils/catalogUtils'; // Importar transformador y tipo Raw
import { z } from 'zod';
import ProductCard from './ProductCard';
import { industries } from '../utils'; // 'industries' podría necesitar revisión si se relaciona con la nueva estructura
import { catalogStructure } from '../config/catalogData'; // Importar la nueva estructura de catálogo
import IndustryFilter from './IndustryFilter';
import { Search, X, Filter } from 'lucide-react';
import Pagination from './Pagination';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { useDebounce } from '../hooks/useDebounce';
// INDUSTRY SELECTOR LOGIC - COMMENTED OUT (UI removed, logic preserved for future reference)
// import IndustrySelector from './IndustrySelector';
import { toast } from 'react-toastify';
import { FEATURE_FLAGS } from '../config/featureFlags';
// import { selectorSubfilterToCatalogIdMap } from '../config/selectorMappings';
// import { industrySubfilters as originalIndustrySubfiltersData } from '../config/industrySubfilters'; // Para obtener los nombres/IDs de los subfiltros del selector
import IndustryBanner from './IndustryBanner';
import type { CatalogCategoryNode } from '../types/catalog';
import { groupCatalogProducts, sortSizes } from '../utils/variantUtils';

// Schema validation
export const ProductSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  price: z.number(),
  category: z.string(),
  industry: z.string().nullable(),
  brand: z.string().nullable(),
  stock: z.number(),
});

interface CatalogProps {
  onAddToCart: (product: CatalogProduct, quantity: number) => void; // Usar CatalogProduct
}

// O usar un hook personalizado más simple
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => setIsMobile(window.innerWidth < 768);
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return isMobile;
};

// Hook to detect screen size for default card size
const useDefaultCardSize = (): 'S' | 'M' | 'L' => {
  const [defaultSize, setDefaultSize] = useState<'S' | 'M' | 'L'>('L');

  useEffect(() => {
    const determineDefaultSize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const isPortrait = height > width;

      // Mobile portrait: S
      if (width < 768 && isPortrait) {
        setDefaultSize('S');
      }
      // Tablet/small laptop landscape (1366x768 or similar): M
      else if (width >= 768 && width <= 1440) {
        setDefaultSize('M');
      }
      // Large desktop: L
      else {
        setDefaultSize('L');
      }
    };

    determineDefaultSize();
    window.addEventListener('resize', determineDefaultSize);
    return () => window.removeEventListener('resize', determineDefaultSize);
  }, []);

  return defaultSize;
};

const Catalog: React.FC<CatalogProps> = ({ onAddToCart }) => {
  const { isAuthenticated } = useAuth();
  const isMobile = useIsMobile();
  const defaultCardSize = useDefaultCardSize();

  // Función para manejar la adición al carrito
  const handleAddToCart = useCallback((product: CatalogProduct, quantity: number) => { // Usar CatalogProduct
    if (FEATURE_FLAGS.ALLOW_GUEST_CART || isAuthenticated) {
      onAddToCart(product, quantity);
    } else {
      // Redirigir al login o mostrar mensaje
      toast.error('Debes iniciar sesión para agregar productos al carrito');
    }
  }, [isAuthenticated, onAddToCart]);

  // Estados
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSizeOptions = [8, 16, 24, 32, 48];
  const [itemsPerPage, setItemsPerPage] = useState(16);
  const [showFilters, setShowFilters] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [itemSize, setItemSize] = useState<'S' | 'M' | 'L'>(defaultCardSize);
  const [sortBy, setSortBy] = useState<'name-asc' | 'name-desc' | 'price-asc' | 'price-desc' | 'newest'>('name-asc');

  // Update itemSize when defaultCardSize changes (on resize)
  useEffect(() => {
    setItemSize(defaultCardSize);
  }, [defaultCardSize]);

  // Estados de filtro que se sincronizarán con la URL
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>(() => {
    const categoryParam = searchParams.get('category');
    const subcategoryParam = searchParams.get('subcategory');
    const categoriesParam = searchParams.get('categories');
    const ids: string[] = [];
    if (categoryParam) {
      ids.push(decodeURIComponent(categoryParam));
      if (subcategoryParam) {
        ids.push(decodeURIComponent(subcategoryParam));
      }
    } else if (categoriesParam) {
      decodeURIComponent(categoriesParam).split(',').forEach(id => {
        if (id.trim()) ids.push(id.trim());
      });
    }
    return ids;
  });
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>(() => {
    const industryParam = searchParams.get('industry');
    return industryParam ? decodeURIComponent(industryParam).toUpperCase().split(',') : [];
  });
  const [selectedBrands, setSelectedBrands] = useState<string[]>(() => {
    const brandParam = searchParams.get('brand');
    return brandParam ? decodeURIComponent(brandParam).toUpperCase().split(',') : [];
  });
  const [selectedSizes, setSelectedSizes] = useState<string[]>(() => {
    const sizeParam = searchParams.get('size') || searchParams.get('sizes');
    return sizeParam ? decodeURIComponent(sizeParam).split(',').filter(Boolean) : [];
  });
  const [localSearch, setLocalSearch] = useState(searchParams.get('search') || '');
  // INDUSTRY SELECTOR STATES - COMMENTED OUT (UI removed, logic preserved for future reference)
  // const [selectedIndustryView, setSelectedIndustryView] = useState<string | null>(searchParams.get('industry') ? decodeURIComponent(searchParams.get('industry')!).toUpperCase().split(',')[0] : null);
  // const [isIndustrySelectorCollapsed, setIsIndustrySelectorCollapsed] = useState(false);
  const debouncedSearch = useDebounce(localSearch, 300);

  // Asegurar que expandedCategories esté definido
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Efecto para expandir categorías cuando se navega desde el navbar
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    const subcategoryParam = searchParams.get('subcategory');
    
    if (categoryParam) {
      setExpandedCategories(prev => {
        const newExpanded = new Set(prev);
        const decodedCategory = decodeURIComponent(categoryParam);
        const decodedSubcategory = subcategoryParam ? decodeURIComponent(subcategoryParam) : null;
        
        // Función para encontrar la ruta completa hacia una categoría/subcategoría
        const findCategoryPath = (nodes: CatalogCategoryNode[], targetId: string, path: string[] = []): string[] | null => {
          for (const node of nodes) {
            const currentPath = [...path, node.id];
            
            if (node.id === targetId) {
              return currentPath;
            }
            
            if (node.children) {
              const found = findCategoryPath(node.children, targetId, currentPath);
              if (found) return found;
            }
          }
          return null;
        };
        
        // SIEMPRE expandir EPP si estamos navegando a una categoría EPP
        newExpanded.add('epp');
        
        // Expandir la ruta hacia la categoría seleccionada
        const categoryPath = findCategoryPath(catalogStructure, decodedCategory);
        if (categoryPath) {
          // Expandir TODOS los nodos en la ruta (incluyendo padres)
          categoryPath.forEach(id => newExpanded.add(id));
        }
        
        // Si hay subcategoría, expandir también su ruta
        if (decodedSubcategory) {
          const subcategoryPath = findCategoryPath(catalogStructure, decodedSubcategory);
          if (subcategoryPath) {
            // Expandir TODOS los nodos en la ruta de la subcategoría
            subcategoryPath.forEach(id => newExpanded.add(id));
          }
        }
        
        console.log('Expandiendo categorías:', Array.from(newExpanded));
        return newExpanded;
      });
    }
  }, [searchParams]);

  // Función para manejar expansión/colapso de categorías
  const handleCategoryToggle = useCallback((categoryId: string) => {
    setExpandedCategories(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(categoryId)) {
        newExpanded.delete(categoryId);
      } else {
        newExpanded.add(categoryId);
      }
      return newExpanded;
    });
  }, []);

  // INDUSTRY SELECTOR SUBFILTERS - COMMENTED OUT (UI removed, logic preserved for future reference)
  // Definir los subfiltros por industria (similar a IndustrySelector)
  // const industrySubfilters = {
  //   'MINERIA': ['Cascos', 'Guantes', 'Calzado'],
  //   'CONSTRUCCION': ['Cascos', 'Arnés', 'Chalecos', 'Guantes', 'Calzado', 'Anteojos'],
  //   'MANUFACTURA': ['Guantes', 'Mascarillas', 'Protección auditiva'],
  //   'PETROLERA': ['Trajes ignífugos', 'Respiradores', 'Cascos'],
  //   'QUIMICA': ['Trajes químicos', 'Respiradores', 'Guantes químicos'],
  //   'METALMECANICA': ['Protección facial', 'Guantes', 'Delantales'],
  //   'LOGISTICA': ['Fajas', 'Guantes', 'Calzado de seguridad'],
  // };

  // INDUSTRY SELECTOR HANDLERS - COMMENTED OUT (UI removed, logic preserved for future reference)
  // const handleIndustrySelect = (industry: string) => {
  //   setSelectedIndustryView(industry);
  //   handleIndustryChange(industry); // Esto actualiza selectedIndustries
  //   // No limpiar selectedSubfilters aquí, ya que no existe más de esa forma.
  //   // La selección de subfiltros del selector ahora afectará directamente a selectedCategoryIds.
  //   setIsIndustrySelectorCollapsed(false);
  // };

  // const handleSelectorSubfilterSelect = (subfilterNameFromSelector: string) => {
  //   if (selectedIndustryView) {
  //     const industryKey = selectedIndustryView.toUpperCase();
  //     const catalogId = selectorSubfilterToCatalogIdMap[industryKey]?.[subfilterNameFromSelector];
  //
  //     if (catalogId) {
  //       // Si el ID de categoría no está ya en selectedCategoryIds, lo añadimos.
  //       // Esto permite que el usuario seleccione múltiples categorías a través de este mecanismo si lo desea.
  //       // Si se quisiera que solo una categoría (la del subfiltro) esté activa,
  //       // se podría hacer setSelectedCategoryIds([catalogId])
  //       if (!selectedCategoryIds.includes(catalogId)) {
  //         // Llamar a handleCategoryChange para añadirlo a selectedCategoryIds y resetear página
  //          handleCategoryChange(catalogId);
  //       }
  //     } else {
  //       console.warn(`No catalog ID mapping for Industry: ${industryKey}, Selector Subfilter: ${subfilterNameFromSelector}`);
  //     }
  //   }
  // };

  // INDUSTRY SELECTOR BACK HANDLER - COMMENTED OUT (UI removed, logic preserved for future reference)
  // Al volver al selector de industrias, limpiar industrias y subfiltros seleccionados
  // const handleBackToIndustries = () => {
  //   if (selectedIndustryView) {
  //     const industryToRemove = selectedIndustryView;
  //     setSelectedIndustries(prev => prev.filter(ind => ind !== industryToRemove));
  //   }
  //   setSelectedIndustryView(null);
  //   // No es necesario limpiar selectedCategoryIds aquí, el usuario decide si los mantiene.
  // };

  // INDUSTRY SELECTOR ACTIVE SUBFILTERS - COMMENTED OUT (UI removed, logic preserved for future reference)
  // Ya no necesitamos subfiltersForSelectorUI, ya que IndustrySelector obtiene su lista internamente.

  // Determinar qué subfiltros del selector (por NOMBRE) están "activos"
  // porque su categoría mapeada está en selectedCategoryIds.
  // Esto se pasa a IndustrySelector para que pueda marcar visualmente los subfiltros seleccionados.
  // const activeSelectorSubfilterNames = useMemo(() => {
  //   if (!selectedIndustryView || selectedCategoryIds.length === 0) {
  //     return [];
  //   }
  //
  //   const industryKey = selectedIndustryView.toUpperCase();
  //   const industryMapping = selectorSubfilterToCatalogIdMap[industryKey];
  //   if (!industryMapping) {
  //     return [];
  //   }

  //   const activeNames: string[] = [];
  //   const originalSubfiltersForCurrentIndustry = originalIndustrySubfiltersData[industryKey] || [];

  //   originalSubfiltersForCurrentIndustry.forEach((subfilterItem: { name: string }) => {
  //     const mappedCatalogId = industryMapping[subfilterItem.name];
  //     if (mappedCatalogId && selectedCategoryIds.includes(mappedCatalogId)) {
  //       activeNames.push(subfilterItem.name);
  //     }
  //   });
  //   return activeNames;
  // }, [selectedIndustryView, selectedCategoryIds, selectorSubfilterToCatalogIdMap, originalIndustrySubfiltersData]);

  // Obtener la industria seleccionada para el banner
  const selectedIndustryForBanner = useMemo(() => {
    console.log('Processing selectedIndustries:', {
      selectedIndustries,
      length: selectedIndustries.length
    });
    
    if (selectedIndustries.length === 1) {
      const industry = selectedIndustries[0].toUpperCase();
      console.log('Selected industry for banner:', industry);
      return industry;
    }
    return null;
  }, [selectedIndustries]);

  // Añade este useEffect para monitorear cambios
  useEffect(() => {
    console.log('Selected industry for banner changed:', selectedIndustryForBanner);
  }, [selectedIndustryForBanner]);

  // INDUSTRY SELECTOR COLLAPSE HANDLER - COMMENTED OUT (UI removed, logic preserved for future reference)
  // Add a function to handle collapse toggle from IndustrySelector
  // const handleIndustrySelectorCollapse = (collapsed: boolean) => {
  //   setIsIndustrySelectorCollapsed(collapsed);
  // };

  // 2. Query client
  const queryClient = useQueryClient();

  // Efecto para sincronizar los filtros DESDE los parámetros de la URL
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    const subcategoryParam = searchParams.get('subcategory');
    const categoriesUrlParam = searchParams.get('categories');
    const industryParam = searchParams.get('industry');
    const brandParam = searchParams.get('brand');
    const searchUrlParam = searchParams.get('search');

    let hasChanged = false;

    // Sincronizar categorías
    const newCategoryIdsFromUrl: string[] = [];
    if (categoryParam) {
      newCategoryIdsFromUrl.push(decodeURIComponent(categoryParam));
      if (subcategoryParam) {
        newCategoryIdsFromUrl.push(decodeURIComponent(subcategoryParam));
      }
    } else if (categoriesUrlParam) {
      decodeURIComponent(categoriesUrlParam).split(',').forEach(id => {
        if (id.trim()) newCategoryIdsFromUrl.push(id.trim());
      });
    }

    setSelectedCategoryIds(currentSelectedIds => {
      const sortedNewUrlIds = [...newCategoryIdsFromUrl].sort();
      const sortedCurrentSelectedIds = [...currentSelectedIds].sort();
      if (JSON.stringify(sortedNewUrlIds) !== JSON.stringify(sortedCurrentSelectedIds)) {
        hasChanged = true;
        return newCategoryIdsFromUrl;
      }
      return currentSelectedIds;
    });

    // Sincronizar industrias
    const newIndustriesFromUrl = industryParam ? decodeURIComponent(industryParam).toUpperCase().split(',') : [];
    setSelectedIndustries(currentSelectedIndustries => {
      if (JSON.stringify(newIndustriesFromUrl.sort()) !== JSON.stringify([...currentSelectedIndustries].sort())) {
        hasChanged = true;
        return newIndustriesFromUrl;
      }
      return currentSelectedIndustries;
    });

    // INDUSTRY SELECTOR URL SYNC - COMMENTED OUT (UI removed, logic preserved for future reference)
    // Sincronizar selectedIndustryView si es relevante
    // if (FEATURE_FLAGS.USE_INDUSTRY_SELECTOR) {
    //     const singleIndustryFromUrl = newIndustriesFromUrl.length === 1 ? newIndustriesFromUrl[0] : null;
    //     if (selectedIndustryView !== singleIndustryFromUrl) {
    //         setSelectedIndustryView(singleIndustryFromUrl);
    //     }
    // } else if (selectedIndustryView !== null) {
    //     setSelectedIndustryView(null); // Limpiar si no se usa el selector
    // }

    // Sincronizar marcas
    const newBrandsFromUrl = brandParam ? decodeURIComponent(brandParam).toUpperCase().split(',') : [];
    setSelectedBrands(currentSelectedBrands => {
      if (JSON.stringify(newBrandsFromUrl.sort()) !== JSON.stringify([...currentSelectedBrands].sort())) {
        hasChanged = true;
        return newBrandsFromUrl;
      }
      return currentSelectedBrands;
    });

    // Sincronizar búsqueda local inmediatamente
    const newSearchFromUrl = searchUrlParam || '';
    if (localSearch !== newSearchFromUrl) {
      setLocalSearch(newSearchFromUrl);
      hasChanged = true;
    }

    if (hasChanged) {
      setCurrentPage(1); // Resetear paginación si algún filtro cambió
    }
  }, [searchParams]); // Solo depende de searchParams

  // 3. Fetch products function
  // Esta función parece no estar siendo utilizada por el useQuery principal.
  // Si se usa en otro lugar, también necesitará ser adaptada para CatalogProduct.
  // Por ahora, nos enfocamos en el useQuery que sí se usa para poblar productsResponse.
  const fetchProducts = useCallback(async (filters: ProductFilters): Promise<PaginatedResponse<CatalogProduct>> => {
    const { search, industries } = filters; // 'categories' también está en ProductFilters
    try {
      let query = supabase! // Asumimos que supabase está inicializado
        .from('products')
        .select('*', { count: 'exact' });

      if (search) {
        query = query.ilike('name', `%${search}%`);
      }

      if (industries && industries.length > 0) {
        // Asumiendo que 'industry' en la DB es un string simple, no un array.
        // Si 'industry' es un array en la DB, usar 'cs' o 'ov' (contains, overlaps)
        query = query.in('industry', industries);
      }
      
      // Aquí se podría añadir filtrado por categorías si 'filters.categories' se usa
      // y si el campo 'categories' en la DB (que es text[]) contiene los IDs de catalogStructure.
      // if (filters.categories && filters.categories.length > 0) {
      //   query = query.cs('categories', filters.categories); // 'categories' CONTAINS selectedCategoryIds
      // }


      const { data, error, count } = await query;

      if (error) throw error;
      if (!data) return { data: [], count: 0 };

      const transformedData = data.map((p: RawProductDataForSupabase) => transformRawProductToCatalogProduct(p));
      
      return {
        data: transformedData,
        count: count || transformedData.length
      };
    } catch (error) {
      console.error('Error fetching products:', error);
      return { data: [], count: 0 };
    }
  }, []);

  // 4. Query hook
  const {
    data: productsResponse,
    isLoading,
    error,
    isFetching
  } = useQuery<{ data: CatalogProduct[]; count: number }, Error>({ // Usar CatalogProduct
    queryKey: ['products'], // Considerar añadir filtros a la queryKey si la queryFn los usa
    queryFn: async () => {
      const PAGE_SIZE = 1000;
      let allData: RawProductDataForSupabase[] = [];
      let from = 0;
      let hasMore = true;

      while (hasMore) {
        const to = from + PAGE_SIZE - 1;
        const { data, error } = await supabase!
          .from('products')
          .select('*')
          .range(from, to);

        if (error) throw error;
        if (data && data.length > 0) {
          allData = allData.concat(data as RawProductDataForSupabase[]);
          if (data.length < PAGE_SIZE) {
            hasMore = false;
          } else {
            from += PAGE_SIZE;
          }
        } else {
          hasMore = false;
        }
      }

      const transformedData = allData.map((p: RawProductDataForSupabase) => transformRawProductToCatalogProduct(p));
      const groupedData = groupCatalogProducts(transformedData);
      return { data: groupedData, count: groupedData.length };
    },
    staleTime: 15 * 60 * 1000, // 15 minutos en memoria para búsquedas instantáneas
    gcTime: 30 * 60 * 1000,
  });

  // Crear un mapa de ID de categoría a nombre de categoría para búsqueda eficiente
  const categoryIdToNameMap = useMemo(() => {
    const map = new Map<string, string>();
    const populateMap = (nodes: CatalogCategoryNode[]) => {
      for (const node of nodes) {
        map.set(node.id, node.name);
        if (node.children) {
          populateMap(node.children);
        }
      }
    };
    populateMap(catalogStructure); // catalogStructure se importa y es constante
    return map;
  }, []);

  // Agregar después de categoryIdToNameMap y antes del filteredProducts
  const sortedCategories = useMemo(() => {
    // Función para ordenar categorías con seleccionadas primero
    const sortCategoriesWithSelectedFirst = (
      categories: CatalogCategoryNode[], 
      selectedCategories: string[]
    ): CatalogCategoryNode[] => {
      return categories
        .map(category => ({
          ...category,
          children: category.children ? sortCategoriesWithSelectedFirst(category.children, selectedCategories) : undefined
        }))
        .sort((a, b) => {
          const aSelected = selectedCategories.includes(a.id);
          const bSelected = selectedCategories.includes(b.id);
          
          if (aSelected && !bSelected) return -1;
          if (!aSelected && bSelected) return 1;
          return 0;
        });
    };

    return sortCategoriesWithSelectedFirst(catalogStructure, selectedCategoryIds);
  }, [selectedCategoryIds]);

  const sortedIndustries = useMemo(() => {
    return [...industries].sort((a, b) => {
      const aSelected = selectedIndustries.includes(a.name);
      const bSelected = selectedIndustries.includes(b.name);
      
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return 0;
    });
  }, [industries, selectedIndustries]);

  const uniqueBrands = useMemo(() => {
    if (!productsResponse?.data) return [];
    return Array.from(new Set(
      productsResponse.data
        .map(product => product.brand?.toUpperCase()) // Convertir a mayúsculas
        .filter(Boolean) as string[] // Asegurar que el resultado es string[]
    )).sort();
  }, [productsResponse?.data]);

  const sortedBrands = useMemo(() => {
    return [...uniqueBrands].sort((a, b) => {
      const aSelected = selectedBrands.includes(a);
      const bSelected = selectedBrands.includes(b);
      
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return 0;
    });
  }, [uniqueBrands, selectedBrands]);

  // Talles disponibles en todo el catálogo
  const availableSizes = useMemo(() => {
    if (!productsResponse?.data) return [];
    const sizesSet = new Set<string>();
    productsResponse.data.forEach(product => {
      product.availableSizes?.forEach(size => sizesSet.add(size));
    });
    return sortSizes(Array.from(sizesSet));
  }, [productsResponse?.data]);

  // 5. Memoized values
  const filteredProducts = useMemo(() => {
    if (!productsResponse?.data) return { data: [], count: 0 };

    const urlCategory = searchParams.get('category');
    const urlSubcategory = searchParams.get('subcategory');

    let filtered = [...productsResponse.data];

    // Filtrar por búsqueda inteligente multi-término y multi-campo
    if (debouncedSearch) {
      const searchTerms = debouncedSearch.toLowerCase().trim().split(/\s+/).filter(Boolean);
      filtered = filtered.filter(product => {
        const productName = (product.name || '').toLowerCase();
        const productDesc = (product.description || '').toLowerCase();
        const productBrand = (product.brand || '').toLowerCase();
        const productIndustries = (product.industries || []).map(i => i.toLowerCase()).join(' ');
        const productCategoryNames = (Array.isArray(product.categoryIds) 
          ? product.categoryIds.map(catId => categoryIdToNameMap.get(catId) || '').join(' ') 
          : '').toLowerCase();
        
        const searchableText = `${productName} ${productBrand} ${productIndustries} ${productCategoryNames} ${productDesc}`;
        return searchTerms.every(term => searchableText.includes(term));
      });
    }

    // Aplicar filtros adicionales

    // Ya no necesitamos getCategoryIdsByNames aquí porque selectedCategoryIds almacenará IDs directamente.
    
    if (selectedCategoryIds.length > 0) {
      filtered = filtered.filter(product => {
        const productCats = product.categoryIds || [];

        // Determinar si estamos en el caso de navegación desde EPPCategoryVisualizer
        // con category Y subcategory presentes en la URL, y si esos filtros aún están activos.
        const пришлиСEpPVisualizerСПодкатегорией = 
          urlCategory && 
          urlSubcategory &&
          selectedCategoryIds.includes(urlCategory) && 
          selectedCategoryIds.includes(urlSubcategory);

        if (пришлиСEpPVisualizerСПодкатегорией) {
          // Filtro AND: el producto debe tener AMBOS, el category y el subcategory de la URL.
          return productCats.includes(urlCategory) && productCats.includes(urlSubcategory);
        } else {
          // Filtro OR: el producto debe tener AL MENOS UNO de los IDs en selectedCategoryIds.
          // Cubre navegación solo con 'category', o selección directa desde IndustryFilter.
          return selectedCategoryIds.some(idToFilter => productCats.includes(idToFilter));
        }
      });
    }
    
    if (selectedIndustries.length > 0) {
      filtered = filtered.filter(product =>
        product.industries && product.industries.some(ind => selectedIndustries.includes(ind)) // Adaptar para array de industrias
      );
    }

    if (selectedBrands.length > 0) {
      filtered = filtered.filter(product => 
        product.brand && selectedBrands.includes(product.brand.toUpperCase()) // Convertir marca del producto a mayúsculas para comparar
      );
    }

    // Filtrado por talles seleccionados
    if (selectedSizes.length > 0) {
      filtered = filtered.filter(product =>
        product.availableSizes && product.availableSizes.some(size => selectedSizes.includes(size))
      );
    }

    // El filtrado por selectedSubfilters ya no es necesario aquí,
    // porque la selección del subfiltro del selector ahora actualiza selectedCategoryIds,
    // y el filtro principal de categorías (usando selectedCategoryIds) ya se encarga de ello.

    // Calcular paginación
    const totalCount = filtered.length;
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    
    return {
      data: filtered.slice(start, end),
      count: totalCount
    };
  }, [
    productsResponse?.data,
    debouncedSearch,
    selectedCategoryIds,
    selectedIndustries,
    selectedBrands,
    // selectedSubfilters, // Ya no es una dependencia directa del filtro de productos
    currentPage, // Asegúrate que categoryIdToNameMap esté disponible si se mueve este useMemo
    categoryIdToNameMap,
    itemsPerPage,
    searchParams // Añadido searchParams como dependencia
  ]);

  const sortedProducts = useMemo(() => {
    if (!filteredProducts?.data) return { data: [], count: 0 };
    
    const sorted = [...filteredProducts.data];
    
    switch (sortBy) {
      case 'name-asc':
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        sorted.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'price-asc':
        sorted.sort((a, b) => (a.price || 0) - (b.price || 0));
        break;
      case 'price-desc':
        sorted.sort((a, b) => (b.price || 0) - (a.price || 0));
        break;
      case 'newest':
        sorted.sort((a, b) => {
          const dateA = new Date(a.created_at || 0).getTime();
          const dateB = new Date(b.created_at || 0).getTime();
          return dateB - dateA;
        });
        break;
    }
    
    return {
      data: sorted,
      count: filteredProducts.count
    };
  }, [filteredProducts, sortBy]);

  // 6. Debug effect
  useEffect(() => {
    console.log('Current state:', {
      productsResponse,
      isLoading,
      error,
      isFetching,
      filteredProducts
    });
  }, [productsResponse, isLoading, error, isFetching, filteredProducts]);

  // uniqueCategories ahora se basará en catalogStructure
  // Necesitaremos una forma de presentar esto al IndustryFilter,
  // que podría esperar un array plano de strings o una estructura más compleja.
  // Por ahora, vamos a aplanar la estructura para obtener todos los nombres de categoría.
  // Esto es una simplificación; idealmente, IndustryFilter manejaría la jerarquía.
  
  const getCategoryNamesFromStructure = (nodes: typeof catalogStructure): string[] => {
    let names: string[] = [];
    nodes.forEach(node => {
      names.push(node.name); // Usar el nombre para mostrar
      if (node.children) {
        names = names.concat(getCategoryNamesFromStructure(node.children));
      }
    });
    return names;
  };

  const uniqueCategoriesForDisplay = useMemo(() => {
    // Esta es una lista plana de nombres para el filtro actual.
    // El filtro de categorías deberá mapear estos nombres a los IDs correctos para la lógica de filtrado.
    return getCategoryNamesFromStructure(catalogStructure);
  }, []);


  // Subfiltros: nombres únicos de productos filtrados por categoría e industria
  const subfilters = useMemo(() => {
    if (!productsResponse?.data) return [];
    let filtered = [...productsResponse.data];
    if (selectedCategoryIds.length > 0) { // Corregido
      filtered = filtered.filter(product =>
        selectedCategoryIds.some((cat: string) => (product.categoryIds || []).includes(cat)) // Corregido y tipado cat
      );
    }
    if (selectedIndustries.length > 0) {
      filtered = filtered.filter(product =>
        // Adaptar para array de industrias, convirtiendo la industria del producto a mayúsculas
        product.industries && product.industries.some(ind => selectedIndustries.includes(ind.toUpperCase()))
      );
    }
    // Retornar nombres únicos de producto
    return Array.from(new Set(filtered.map(product => product.name)));
  }, [productsResponse?.data, selectedCategoryIds, selectedIndustries]);

  // 6. Event handlers
  // handleSubfilterChange y el estado selectedSubfilters se eliminaron.
  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearch(value);
    // No actualizar searchParams aquí
  }, []);

  const handleIndustryChange = useCallback((industry: string) => {
    const upperCaseIndustry = industry.toUpperCase(); // Corregido: usar industry en lugar de brand
    setSelectedIndustries((prev: string[]) => 
      {
        const newSelected = prev.includes(upperCaseIndustry) ? prev.filter(i => i !== upperCaseIndustry) : [...prev, upperCaseIndustry];
        setSearchParams(currentParams => {
          const newParams = new URLSearchParams(currentParams);
          if (newSelected.length > 0) {
            newParams.set('industry', newSelected.join(','));
          } else {
            newParams.delete('industry');
          }
          if (newParams.toString() !== currentParams.toString()) {
            setCurrentPage(1);
            return newParams;
          }
          return currentParams;
        }, { replace: true });
        return newSelected;
      }
    );
    // INDUSTRY SELECTOR FILTER SYNC - COMMENTED OUT (UI removed, logic preserved for future reference)
    // Si se usa IndustrySelector, y se cambia la industria desde el filtro IndustryFilter,
    // actualizamos selectedIndustryView si solo queda una industria seleccionada.
    // if (FEATURE_FLAGS.USE_INDUSTRY_SELECTOR) {
    //     setSelectedIndustries(currentSelected => {
    //         if (currentSelected.length === 1) {
    //             setSelectedIndustryView(currentSelected[0]);
    //         } else {
    //             setSelectedIndustryView(null);
    //         }
    //         return currentSelected; // Devuelve el estado actual para que el flujo continúe
    //     });
    // }
  }, [setSearchParams]);

  const handleBrandChange = useCallback((brand: string) => {
    const upperCaseBrand = brand.toUpperCase(); // Convertir a mayúsculas
    setSelectedBrands((prev: string[]) => 
      {
        const newSelected = prev.includes(upperCaseBrand) ? prev.filter(b => b !== upperCaseBrand) : [...prev, upperCaseBrand];
        setSearchParams(currentParams => {
          const newParams = new URLSearchParams(currentParams);
          if (newSelected.length > 0) {
            newParams.set('brand', newSelected.join(','));
          } else {
            newParams.delete('brand');
          }
          if (newParams.toString() !== currentParams.toString()) {
            setCurrentPage(1);
            return newParams;
          }
          return currentParams;
        }, { replace: true });
        return newSelected;
      }
    );
  }, [setSearchParams, setSelectedBrands, setCurrentPage]); // Añadidas dependencias

  const handleCategoryChange = useCallback((categoryId: string) => { // Ahora recibe y maneja IDs
    setSelectedCategoryIds((prev: string[]) =>
      {
        const newSelected = prev.includes(categoryId) ? prev.filter(id => id !== categoryId) : [...prev, categoryId];
        setSearchParams(currentParams => {
          const newParams = new URLSearchParams(currentParams);
          newParams.delete('category'); // Limpiar params específicos si se usa el filtro general
          newParams.delete('subcategory');
          if (newSelected.length > 0) {
            newParams.set('categories', newSelected.join(','));
          } else {
            newParams.delete('categories');
          }
          if (newParams.toString() !== currentParams.toString()) {
            setCurrentPage(1);
            return newParams;
          }
          return currentParams;
        }, { replace: true });
        return newSelected;
      }
    );
  }, [setSearchParams, setSelectedCategoryIds, setCurrentPage]); // Añadidas dependencias

  const handleSizeChange = useCallback((size: string) => {
    setSelectedSizes((prev: string[]) => {
      const newSelected = prev.includes(size) ? prev.filter(s => s !== size) : [...prev, size];
      setSearchParams(currentParams => {
        const newParams = new URLSearchParams(currentParams);
        if (newSelected.length > 0) {
          newParams.set('size', newSelected.join(','));
        } else {
          newParams.delete('size');
          newParams.delete('sizes');
        }
        if (newParams.toString() !== currentParams.toString()) {
          setCurrentPage(1);
          return newParams;
        }
        return currentParams;
      }, { replace: true });
      return newSelected;
    });
  }, [setSearchParams, setCurrentPage]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset a la primera página cuando cambia el número de items por página
  }, []);

  const handleLocalSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalSearch(value);
    setCurrentPage(1);
  }, []);

  // handleSubfilterSelect se renombró a handleSelectorSubfilterSelect y su lógica cambió.
  // La prop onSubfilterSelect del IndustrySelector se conectará a handleSelectorSubfilterSelect.
  
  // Efecto para sincronizar localSearch con el parámetro de URL inmediatamente
  useEffect(() => {
    const searchUrlParam = searchParams.get('search');
    const sortUrlParam = searchParams.get('sort') as typeof sortBy;
    
    setLocalSearch(searchUrlParam || '');
    if (sortUrlParam) {
      setSortBy(sortUrlParam);
    }
  }, [searchParams]); // Reacciona a cualquier cambio en searchParams

  // Efecto separado para actualizar URL cuando cambia debouncedSearch
  useEffect(() => {
    if (debouncedSearch === undefined) return;

    setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        if (debouncedSearch) {
            newParams.set('search', debouncedSearch);
        } else {
            newParams.delete('search');
        }
        
        // Solo actualizar si realmente cambió
        if (newParams.toString() !== prev.toString()) {
            setCurrentPage(1);
            return newParams;
        }
        return prev;
    }, { replace: true });
    
    // Sincronizar el estado global de búsqueda
    setSearch(debouncedSearch);
  }, [debouncedSearch, setSearchParams, setSearch]);

  if (isLoading || isFetching) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-lg text-gray-600">Cargando productos...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-lg text-red-500">
          Error al cargar productos: {(error as Error).message}
        </div>
      </div>
    );
  }

  // Verificar si tenemos datos antes de renderizar
  const hasProducts = productsResponse?.data && productsResponse.data.length > 0;
  const displayProducts = sortedProducts?.data || [];

  return (
    <div className="container mx-auto px-4 pt-4 md:pt-6">
      {/* Industry Banner - Solo desktop */}
      <div className="my-6 hidden md:block">
        <IndustryBanner
          selectedIndustry={selectedIndustryForBanner}
          selectedIndustriesCount={selectedIndustries.length}
          totalProductsCount={filteredProducts.count}
        />
      </div>

      {/* Layout principal */}
      <div className="flex flex-col lg:flex-row gap-8 mt-6">
        {/* Sidebar desktop */}
        <aside className="hidden lg:block w-72 flex-shrink-0">
          <div className="sticky top-24">
            <IndustryFilter
              allIndustries={sortedIndustries}
              selectedIndustries={selectedIndustries}
              onIndustryChange={handleIndustryChange}
              categoryStructure={sortedCategories}
              selectedCategories={selectedCategoryIds}
              onCategoryChange={handleCategoryChange}
              brands={sortedBrands}
              selectedBrands={selectedBrands}
              onBrandChange={handleBrandChange}
              sizes={availableSizes}
              selectedSizes={selectedSizes}
              onSizeChange={handleSizeChange}
              expandedCategories={expandedCategories}
              onCategoryToggle={handleCategoryToggle}
              className={isMobile ? 'w-full' : ''}
              isMobile={isMobile}
            />
          </div>
        </aside>

        {/* Contenedor de productos con su buscador */}
        <div className="flex-1 min-w-0">
          {/* Buscador destacado con selector de tamaño */}
          <div className="bg-white py-2 border-b border-gray-100 mb-6">
            <div className="flex gap-3 items-center justify-between">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={localSearch}
                  onChange={handleLocalSearchChange}
                  placeholder="Buscar productos por nombre, descripción, marca o categoría..."
                  className="w-full pl-10 pr-4 py-2 bg-white border border-amber-200 rounded-xl 
                           shadow-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent
                           transition-all duration-200"
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-amber-500 w-5 h-5" />
                {localSearch && (
                  <button
                    onClick={() => setLocalSearch('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
              
              {/* Size selector pill - aligned right - solo desktop */}
              <div className="hidden md:flex bg-gray-100 rounded-lg p-1 shrink-0 ml-4">
                {(['S', 'M', 'L'] as const).map((size) => (
                  <button
                    key={size}
                    onClick={() => setItemSize(size)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      itemSize === size
                        ? 'bg-white text-amber-600 shadow-sm ring-1 ring-amber-200'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>

              {/* Botón de filtros - solo móvil */}
              <button
                onClick={() => setShowMobileFilters(true)}
                className="md:hidden flex items-center space-x-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors shrink-0 ml-4"
              >
                <Filter className="w-4 h-4" />
                <span>Filtros</span>
              </button>
            </div>
          </div>

          {/* Grid de productos con tamaños dinámicos */}
          <div className={`grid ${
            itemSize === 'S'
              ? 'gap-2 grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8'
              : itemSize === 'M'
                ? 'gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6'
                : 'gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5'
          }`}>
            {displayProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onAddToCart={handleAddToCart}
                showAddToCart={FEATURE_FLAGS.ALLOW_GUEST_CART || isAuthenticated}
                size={itemSize}
              />
            ))}
          </div>

          {!isAuthenticated && !FEATURE_FLAGS.ALLOW_GUEST_CART && (
            <div className="mt-8 p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-gray-700">
                Para agregar productos al carrito, por favor{' '}
                <Link to="/login" className="text-amber-600 hover:text-amber-700 font-medium">
                  inicia sesión
                </Link>
              </p>
            </div>
          )}

          {/* Paginación */}
          <div className="mt-8 mb-16">
            <Pagination
              currentPage={currentPage}
              totalPages={Math.ceil((sortedProducts?.count || 0) / itemsPerPage)}
              onPageChange={handlePageChange}
              itemsPerPage={itemsPerPage}
              itemsPerPageOptions={pageSizeOptions}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        </div>
      </div>

      {/* Modal de filtros móviles */}
      {showMobileFilters && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div 
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowMobileFilters(false)}
          />
          <div className="absolute right-0 top-0 h-full w-80 bg-white shadow-xl overflow-y-auto">
            <div className="p-4">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Filtros</h3>
                <button
                  onClick={() => setShowMobileFilters(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <IndustryFilter
                allIndustries={industries}
                selectedIndustries={selectedIndustries}
                onIndustryChange={handleIndustryChange}
                brands={uniqueBrands}
                selectedBrands={selectedBrands}
                onBrandChange={handleBrandChange}
                categoryStructure={catalogStructure}
                selectedCategories={selectedCategoryIds}
                onCategoryChange={handleCategoryChange}
                sizes={availableSizes}
                selectedSizes={selectedSizes}
                onSizeChange={handleSizeChange}
              />
              
              {/* Selector de tamaño de cards en móvil */}
              {/* 
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Tamaño de tarjetas</h4>
                <div className="flex bg-gray-100 rounded-lg p-1">
                  {(['S', 'M', 'L'] as const).map((size) => (
                    <button
                      key={size}
                      onClick={() => setItemSize(size)}
                      className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        itemSize === size
                          ? 'bg-white text-amber-600 shadow-sm ring-1 ring-amber-200'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
              */}

              {/* Botón aplicar filtros */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowMobileFilters(false)}
                  className="w-full bg-amber-600 text-white py-3 rounded-lg hover:bg-amber-700 transition-colors"
                >
                  Aplicar Filtros
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Catalog;
